import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Checkbox,
  Dialog,
  Flex,
  RadioGroup,
  SegmentedControl,
  Select,
  Table,
  Text,
  TextField,
  Tooltip,
} from "@radix-ui/themes";
import { ChatBubbleIcon, ChevronLeftIcon, ChevronRightIcon, EnvelopeClosedIcon, InfoCircledIcon, MobileIcon, Pencil1Icon, PlusIcon, TrashIcon } from "@radix-ui/react-icons";
import { ToastAlert } from "components";
import { DateTimePicker } from "components/datepicker/DateTimePicker";
import { useProfileContext } from "context";
import { useCampaignAudienceEstimate, useCampaigns, useContactTags, useCreateCampaign, useDeleteCampaign, useGetWhatsappAccountPhoneNumbers, useGetWhatsappCredentialAccounts, useMarketingWhatsappTemplates, useSegments, useUpdateCampaign } from "hooks";
import { useToast } from "../../context/ToastContext";
import MarketingWhatsappPreview from "./MarketingWhatsappPreview";
import WhatsappMessagingLimitHelp from "./WhatsappMessagingLimitHelp";

const STEP_TITLES = ["Campaign", "Audience", "Schedule", "Review"];
const WHATSAPP_CAMPAIGN_INTEGRATION_REQUIRED_MESSAGE = "Connect a WhatsApp integration before creating WhatsApp campaigns.";
const STATUS_COLORS = {
  scheduled: "amber",
  queued: "blue",
  processing: "blue",
  paused_limit: "amber",
  paused: "amber",
  pausing: "amber",
  cancelled: "gray",
  sent: "green",
  failed: "red",
};

function formatDateTime(value) {
  if (!value) return "Send now";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Invalid date";

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatCampaignDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatMessagingLimit(account) {
  const usage = account?.messaging_limit_usage || {};
  const tier = account?.messaging_limit_tier || usage.tier || "Not refreshed";
  const limit = account?.messaging_limit ?? usage.limit;
  const used = Number(usage.used || 0);
  const remaining = usage.remaining;

  if (limit === null || typeof limit === "undefined") {
    return {
      tier,
      detail: usage.limit_known ? "Meta reports an unlimited messaging tier." : "Messaging limit is unknown. Sending stays paused until the limit is confirmed.",
      color: "gray",
    };
  }

  return {
    tier,
    detail: `${Number(remaining || 0).toLocaleString()} of ${Number(limit || 0).toLocaleString()} unique recipients remaining in the rolling 24-hour window (${used.toLocaleString()} used).`,
    color: Number(remaining || 0) > 0 ? "green" : "red",
  };
}

function extractErrorMessage(error, fallbackMessage) {
  const responseData = error?.response?.data;
  return responseData?.error
    || responseData?.detail
    || responseData?.name?.[0]
    || responseData?.whatsapp_template?.[0]
    || responseData?.whatsapp_phone_number_id?.[0]
    || responseData?.scheduled_send_at?.[0]
    || fallbackMessage;
}

function getWhatsappPhoneNumberLabel(phoneNumber) {
  if (!phoneNumber) return "";
  return phoneNumber.display_phone_number || phoneNumber.id || "";
}

function TruncatedText({ children, size = "2", weight = "regular", color }) {
  const text = typeof children === "string" ? children : String(children ?? "");
  const content = (
    <Box
      as="div"
      style={{ width: "100%", minWidth: 0, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
    >
      <Text size={size} weight={weight} color={color}>{text}</Text>
    </Box>
  );

  return text && text !== "—" ? <Tooltip content={text}>{content}</Tooltip> : content;
}

function ChannelFilter({ selectedChannel, onSelectedChannelChange }) {
  return (
    <SegmentedControl.Root value={selectedChannel} onValueChange={onSelectedChannelChange}>
      <SegmentedControl.Item value="all">All</SegmentedControl.Item>
      <SegmentedControl.Item value="email">
        <Flex gap="1" align="center">
          <EnvelopeClosedIcon />
          <Text>Email</Text>
        </Flex>
      </SegmentedControl.Item>
      <SegmentedControl.Item value="sms">
        <Flex gap="1" align="center">
          <MobileIcon />
          <Text>SMS</Text>
        </Flex>
      </SegmentedControl.Item>
      <SegmentedControl.Item value="whatsapp">
        <Flex gap="1" align="center">
          <ChatBubbleIcon />
          <Text>Whatsapp</Text>
        </Flex>
      </SegmentedControl.Item>
    </SegmentedControl.Root>
  );
}

function StepBadge({ index, currentStep }) {
  const active = index + 1 === currentStep;
  const complete = index + 1 < currentStep;

  return (
    <Badge variant={active ? "solid" : "soft"} color={complete ? "green" : active ? "blue" : "gray"}>
      {index + 1}. {STEP_TITLES[index]}
    </Badge>
  );
}

function normalizeBackendCampaign(campaign) {
  return {
    id: campaign.campaign_id,
    name: campaign.name,
    audienceType: campaign.audience_type || "all_contacts",
    segmentIds: (campaign.segments || []).map(String),
    tagIds: (campaign.tags || []).map(String),
    audienceLabel: campaign.audience_type === "all_contacts"
      ? "All WhatsApp-reachable contacts"
      : campaign.audience_type === "tag"
        ? campaign.tag_names?.join(", ") || "Selected tags"
        : campaign.segment_names?.join(", ") || "Selected segments",
    whatsappTemplateId: campaign.whatsapp_template ? String(campaign.whatsapp_template) : "",
    templateName: campaign.template_name || "WhatsApp template",
    phoneNumberId: campaign.whatsapp_phone_number_id || "",
    senderPhoneNumber: campaign.whatsapp_sender_phone_number || campaign.whatsapp_phone_number_id || "",
    recipientCount: Number(campaign.estimated_recipients ?? campaign.recipient_count ?? 0),
    deliveredCount: Number(campaign.sent_count ?? 0),
    failedCount: Number(campaign.failed_count ?? 0),
    sendMode: campaign.send_mode,
    scheduledSendAt: campaign.scheduled_send_at,
    sentAt: campaign.sent_at,
    status: campaign.status,
    createdAt: campaign.created_at,
    updatedAt: campaign.updated_at,
  };
}

function getTemplateFormValue(template) {
  return template?.databaseId ? String(template.databaseId) : String(template?.id || "");
}

function buildDefaultForm(templateId = "") {
  return {
    name: "",
    templateId,
    phoneNumberId: "",
    audienceType: "all_contacts",
    segmentIds: [],
    tagIds: [],
    sendMode: "now",
    scheduledSendAt: "",
  };
}

function campaignToForm(campaign) {
  return {
    name: campaign.name || "",
    templateId: campaign.whatsappTemplateId || "",
    phoneNumberId: campaign.phoneNumberId || "",
    audienceType: campaign.audienceType || "all_contacts",
    segmentIds: campaign.segmentIds || [],
    tagIds: campaign.tagIds || [],
    sendMode: campaign.sendMode || "scheduled",
    scheduledSendAt: campaign.scheduledSendAt || "",
  };
}

function canEditCampaign(campaign) {
  return canDeleteCampaign(campaign);
}

function canDeleteCampaign(campaign) {
  return (
    campaign?.status === "scheduled" &&
    campaign?.sendMode === "scheduled" &&
    new Date(campaign?.scheduledSendAt).getTime() > Date.now()
  );
}

export function MarketingWhatsappCampaignDialog({ open, onOpenChange, segments = [], contactTags = [] }) {
  return (
    <MarketingWhatsappCampaigns
      selectedChannel="whatsapp"
      onSelectedChannelChange={() => {}}
      segments={segments}
      contactTags={contactTags}
      createDialogOpen={open}
      onCreateDialogOpenChange={onOpenChange}
      dialogOnly
    />
  );
}

export default function MarketingWhatsappCampaigns({
  selectedChannel,
  onSelectedChannelChange,
  segments: suppliedSegments = [],
  contactTags: suppliedContactTags = [],
  createDialogOpen,
  onCreateDialogOpenChange,
  dialogOnly = false,
}) {
  const navigate = useNavigate();
  const { userProfile } = useProfileContext();
  const { setToastMessage } = useToast();
  const organisationId = userProfile?.organisation_id;
  const [internalCreateDialogOpen, setInternalCreateDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [campaignPage, setCampaignPage] = useState(1);
  const [campaignForm, setCampaignForm] = useState(buildDefaultForm());
  const isCreateDialogControlled = typeof createDialogOpen === "boolean";
  const isCreateDialogOpen = isCreateDialogControlled ? createDialogOpen : internalCreateDialogOpen;
  const setIsCreateDialogOpen = useCallback((open) => {
    if (isCreateDialogControlled) {
      onCreateDialogOpenChange?.(open);
      return;
    }

    setInternalCreateDialogOpen(open);
  }, [isCreateDialogControlled, onCreateDialogOpenChange]);

  const shouldLoadAudienceData = isCreateDialogOpen && currentStep >= 2;
  const { campaigns: backendCampaigns, pagination, isLoading: isLoadingCampaigns } = useCampaigns(organisationId, {
    page: campaignPage,
    pageSize: 25,
    campaignType: "whatsapp",
    enabled: !dialogOnly,
  });
  const { segments: loadedSegments } = useSegments(organisationId, { enabled: shouldLoadAudienceData });
  const { contactTags: loadedContactTags } = useContactTags(organisationId, { enabled: shouldLoadAudienceData });
  const segments = suppliedSegments.length ? suppliedSegments : loadedSegments;
  const contactTags = suppliedContactTags.length ? suppliedContactTags : loadedContactTags;
  const createCampaignMutation = useCreateCampaign();
  const updateCampaignMutation = useUpdateCampaign();
  const deleteCampaignMutation = useDeleteCampaign();
  const { whatsappTemplates: templates, isLoading: isLoadingTemplates } = useMarketingWhatsappTemplates(organisationId);
  const { data: whatsappAccountsData } = useGetWhatsappCredentialAccounts(organisationId);
  const approvedTemplates = useMemo(() => {
    return templates.filter((template) => template.reviewStatus === "approved");
  }, [templates]);
  const isEditingCampaign = Boolean(editingCampaign);
  const isSavingCampaign = createCampaignMutation.isPending || updateCampaignMutation.isPending;

  useEffect(() => {
    if (!isCreateDialogOpen) {
      setCurrentStep(1);
      setEditingCampaign(null);
      setCampaignForm(buildDefaultForm(getTemplateFormValue(approvedTemplates[0])));
    }
  }, [approvedTemplates, isCreateDialogOpen]);

  const campaigns = useMemo(() => {
    return (backendCampaigns || [])
      .filter((campaign) => campaign.campaign_type === "whatsapp")
      .map(normalizeBackendCampaign);
  }, [backendCampaigns]);

  const selectedTemplate = useMemo(() => {
    return approvedTemplates.find((template) => (
      getTemplateFormValue(template) === String(campaignForm.templateId) ||
      String(template.id) === String(campaignForm.templateId)
    )) || null;
  }, [approvedTemplates, campaignForm.templateId]);
  const selectedWhatsappConfigId = selectedTemplate?.whatsappConfig || "";
  const whatsappAccounts = useMemo(() => whatsappAccountsData?.data?.accounts || [], [whatsappAccountsData]);
  const hasWhatsappIntegration = whatsappAccounts.length > 0;
  const selectedWhatsappAccount = useMemo(() => {
    return whatsappAccounts.find((account) => String(account.id) === String(selectedWhatsappConfigId)) || null;
  }, [selectedWhatsappConfigId, whatsappAccounts]);
  const messagingLimit = useMemo(() => formatMessagingLimit(selectedWhatsappAccount), [selectedWhatsappAccount]);
  const { data: phoneNumbersData, isLoading: isLoadingPhoneNumbers } = useGetWhatsappAccountPhoneNumbers(
    organisationId,
    selectedWhatsappConfigId,
    isCreateDialogOpen && Boolean(selectedWhatsappConfigId),
  );
  const whatsappPhoneNumbers = useMemo(() => phoneNumbersData?.data?.phone_numbers || [], [phoneNumbersData]);
  const selectedPhoneNumber = useMemo(() => {
    return whatsappPhoneNumbers.find((phoneNumber) => String(phoneNumber.id) === String(campaignForm.phoneNumberId)) || null;
  }, [campaignForm.phoneNumberId, whatsappPhoneNumbers]);

  useEffect(() => {
    if (!isCreateDialogOpen || isLoadingPhoneNumbers) return;

    setCampaignForm((previous) => {
      if (!selectedTemplate) {
        return previous.phoneNumberId ? { ...previous, phoneNumberId: "" } : previous;
      }

      const senderStillAvailable = whatsappPhoneNumbers.some((phoneNumber) => String(phoneNumber.id) === String(previous.phoneNumberId));
      if (senderStillAvailable) return previous;

      const nextPhoneNumberId = whatsappPhoneNumbers.length === 1 ? String(whatsappPhoneNumbers[0].id) : "";
      if (previous.phoneNumberId === nextPhoneNumberId) return previous;
      return { ...previous, phoneNumberId: nextPhoneNumberId };
    });
  }, [isCreateDialogOpen, isLoadingPhoneNumbers, selectedTemplate, whatsappPhoneNumbers]);

  const selectedSegments = useMemo(() => {
    const selectedIds = new Set(campaignForm.segmentIds.map(String));
    return segments.filter((segment) => selectedIds.has(String(segment.id)));
  }, [campaignForm.segmentIds, segments]);

  const selectedTags = useMemo(() => {
    const selectedIds = new Set(campaignForm.tagIds.map(String));
    return contactTags.filter((tag) => selectedIds.has(String(tag.id)));
  }, [campaignForm.tagIds, contactTags]);

  const { recipientCount: allContactsEstimate } = useCampaignAudienceEstimate(
    organisationId,
    "all_contacts",
    [],
    "whatsapp",
    [],
    { enabled: shouldLoadAudienceData && campaignForm.audienceType === "all_contacts" },
  );
  const { recipientCount: segmentRecipientEstimate } = useCampaignAudienceEstimate(
    organisationId,
    "segment",
    campaignForm.segmentIds,
    "whatsapp",
    [],
    { enabled: shouldLoadAudienceData && campaignForm.audienceType === "segment" },
  );
  const { recipientCount: tagRecipientEstimate } = useCampaignAudienceEstimate(
    organisationId,
    "tag",
    [],
    "whatsapp",
    campaignForm.tagIds,
    { enabled: shouldLoadAudienceData && campaignForm.audienceType === "tag" },
  );

  const recipientEstimate = campaignForm.audienceType === "segment"
    ? segmentRecipientEstimate
    : campaignForm.audienceType === "tag"
      ? tagRecipientEstimate
      : allContactsEstimate;

  const openCreateDialog = useCallback(() => {
    if (!hasWhatsappIntegration) {
      setToastMessage(WHATSAPP_CAMPAIGN_INTEGRATION_REQUIRED_MESSAGE);
      return;
    }

    if (approvedTemplates.length === 0) {
      setToastMessage("Create an approved WhatsApp template before building a campaign");
      return;
    }

    setEditingCampaign(null);
    setCampaignForm(buildDefaultForm(getTemplateFormValue(approvedTemplates[0])));
    setCurrentStep(1);
    setIsCreateDialogOpen(true);
  }, [approvedTemplates, hasWhatsappIntegration, setIsCreateDialogOpen, setToastMessage]);

  useEffect(() => {
    if (!isCreateDialogControlled) {
      return;
    }

    if (!createDialogOpen) {
      return;
    }

    if (isLoadingTemplates) {
      return;
    }

    if (approvedTemplates.length === 0) {
      setToastMessage("Create an approved WhatsApp template before building a campaign");
      setIsCreateDialogOpen(false);
      return;
    }

    setEditingCampaign(null);
    setCampaignForm(buildDefaultForm(getTemplateFormValue(approvedTemplates[0])));
    setCurrentStep(1);
  }, [approvedTemplates, approvedTemplates.length, createDialogOpen, isCreateDialogControlled, isLoadingTemplates, setIsCreateDialogOpen, setToastMessage]);

  const openEditDialog = (campaign) => {
    setEditingCampaign(campaign);
    setCampaignForm(campaignToForm(campaign));
    setCurrentStep(1);
    setIsCreateDialogOpen(true);
  };

  const createDialogMaxWidth = currentStep === STEP_TITLES.length ? 780 : 620;
  const stepIsValid = useMemo(() => {
    if (currentStep === 1) {
      return campaignForm.name.trim().length > 0 && Boolean(campaignForm.templateId) && Boolean(campaignForm.phoneNumberId);
    }
    if (currentStep === 2) {
      if (campaignForm.audienceType === "segment") return campaignForm.segmentIds.length > 0;
      if (campaignForm.audienceType === "tag") return campaignForm.tagIds.length > 0;
      return true;
    }
    if (currentStep === 3) {
      if (campaignForm.sendMode === "now") return true;
      if (!campaignForm.scheduledSendAt) return false;
      return new Date(campaignForm.scheduledSendAt).getTime() > Date.now();
    }
    return true;
  }, [campaignForm, currentStep]);

  const handleToggleSegment = (segmentId, checked) => {
    setCampaignForm((previous) => {
      const currentIds = previous.segmentIds.map(String);
      const nextIds = checked
        ? [...currentIds, String(segmentId)]
        : currentIds.filter((value) => value !== String(segmentId));
      return { ...previous, segmentIds: Array.from(new Set(nextIds)) };
    });
  };

  const handleToggleTag = (tagId, checked) => {
    setCampaignForm((previous) => {
      const currentIds = previous.tagIds.map(String);
      const nextIds = checked
        ? [...currentIds, String(tagId)]
        : currentIds.filter((value) => value !== String(tagId));
      return { ...previous, tagIds: Array.from(new Set(nextIds)) };
    });
  };

  const handleConfirm = async () => {
    if (!selectedTemplate) {
      setToastMessage("Choose a WhatsApp template before saving the campaign");
      return;
    }

    if (!organisationId) {
      setToastMessage("Organisation context is missing for this campaign");
      return;
    }

    if (!selectedTemplate.databaseId) {
      setToastMessage("Refresh the page and choose the WhatsApp template again");
      return;
    }

    if (!campaignForm.phoneNumberId) {
      setToastMessage("Choose a WhatsApp sender phone number before saving the campaign");
      return;
    }

    try {
      const payload = {
        organisation: organisationId,
        name: campaignForm.name.trim(),
        campaign_type: "whatsapp",
        audience_type: campaignForm.audienceType,
        segments: campaignForm.audienceType === "segment" ? campaignForm.segmentIds.map(Number) : [],
        tags: campaignForm.audienceType === "tag" ? campaignForm.tagIds.map(Number) : [],
        whatsapp_template: selectedTemplate.databaseId,
        whatsapp_phone_number_id: campaignForm.phoneNumberId,
        send_mode: campaignForm.sendMode,
        scheduled_send_at: campaignForm.sendMode === "scheduled"
          ? new Date(campaignForm.scheduledSendAt).toISOString()
          : null,
      };

      if (isEditingCampaign) {
        await updateCampaignMutation.mutateAsync({
          campaignId: editingCampaign.id,
          data: payload,
        });
        setToastMessage("Scheduled WhatsApp campaign updated");
      } else {
        await createCampaignMutation.mutateAsync(payload);
        setToastMessage(campaignForm.sendMode === "scheduled" ? "WhatsApp campaign scheduled" : "WhatsApp campaign queued for sending");
      }

      setIsCreateDialogOpen(false);
    } catch (error) {
      setToastMessage(extractErrorMessage(error, "Failed to save WhatsApp campaign"));
    }
  };

  const handleDeleteCampaign = async () => {
    if (!campaignToDelete) return;

    try {
      await deleteCampaignMutation.mutateAsync(campaignToDelete.id);
      setToastMessage("Scheduled WhatsApp campaign deleted");
      setCampaignToDelete(null);
    } catch (error) {
      setToastMessage(extractErrorMessage(error, "Failed to delete WhatsApp campaign"));
    }
  };

  return (
    <>
      {!dialogOnly ? (
      <Flex direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "center", flex: "1 1 auto", minHeight: 0, height: "100%", width: "100%" }}>
        <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
          <ChannelFilter selectedChannel={selectedChannel} onSelectedChannelChange={onSelectedChannelChange} />
          {!hasWhatsappIntegration ? (
            <Tooltip content={WHATSAPP_CAMPAIGN_INTEGRATION_REQUIRED_MESSAGE}>
              <span style={{ display: "inline-flex" }}>
                <Button variant="soft" disabled>
                  <PlusIcon width="16" height="16" />
                  Create WhatsApp campaign
                </Button>
              </span>
            </Tooltip>
          ) : approvedTemplates.length === 0 || isLoadingTemplates ? (
            <Tooltip content="Create an approved WhatsApp template before building a campaign">
              <span style={{ display: "inline-flex" }}>
                <Button variant="soft" disabled>
                  <PlusIcon width="16" height="16" />
                  Create WhatsApp campaign
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button variant="soft" onClick={openCreateDialog}>
              <PlusIcon width="16" height="16" />
              Create WhatsApp campaign
            </Button>
          )}
        </Flex>

        {!hasWhatsappIntegration ? (
          <Flex direction="column" gap="3" align="center" justify="center" style={{ alignSelf: "stretch", border: "1px dashed var(--gray-a6)", borderRadius: "var(--radius-4)", flex: "1 1 0", minHeight: 0, height: "100%", padding: "var(--space-6)" }}>
            <Text size="4" weight="medium">Connect a WhatsApp integration before building a campaign</Text>
            <Text size="2" color="gray" align="center">WhatsApp campaigns need a connected WhatsApp account before templates, senders, or campaigns can be created.</Text>
          </Flex>
        ) : approvedTemplates.length === 0 ? (
          <Flex direction="column" gap="3" align="center" justify="center" style={{ alignSelf: "stretch", border: "1px dashed var(--gray-a6)", borderRadius: "var(--radius-4)", flex: "1 1 0", minHeight: 0, height: "100%", padding: "var(--space-6)" }}>
            <Text size="4" weight="medium">Create an approved WhatsApp template before building a campaign</Text>
            <Text size="2" color="gray" align="center">WhatsApp campaigns use the account already associated with the selected template.</Text>
            <Button onClick={() => navigate("/marketing/templates", { state: { selectedSegment: "whatsapp" } })}>Open WhatsApp templates</Button>
          </Flex>
        ) : isLoadingCampaigns ? (
          <Flex direction="column" gap="3" align="center" justify="center" style={{ alignSelf: "stretch", border: "1px dashed var(--gray-a6)", borderRadius: "var(--radius-4)", flex: "1 1 0", minHeight: 0, height: "100%", padding: "var(--space-6)" }}>
            <Text size="4" weight="medium">Loading WhatsApp campaigns</Text>
          </Flex>
        ) : campaigns.length === 0 ? (
          <Flex direction="column" gap="3" align="center" justify="center" style={{ alignSelf: "stretch", border: "1px dashed var(--gray-a6)", borderRadius: "var(--radius-4)", flex: "1 1 0", minHeight: 0, height: "100%", padding: "var(--space-6)" }}>
            <Text size="4" weight="medium">No WhatsApp campaigns yet</Text>
            <Text size="2" color="gray" align="center">Build a campaign with template selection, audience controls, and send timing.</Text>
            <Button onClick={openCreateDialog}>Create WhatsApp campaign</Button>
          </Flex>
        ) : (
          <>
          <Card style={{ display: "flex", alignSelf: "stretch", flexDirection: "column", flex: "1 1 auto", width: "100%", minWidth: 0, minHeight: 0, height: "100%", overflow: "hidden" }}>
            <Box style={{ flex: 1, width: "100%", minWidth: 0, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
              <Table.Root layout="fixed" style={{ width: "100%", minWidth: 0 }}>
                <Table.Header style={{ position: "sticky", top: 0, zIndex: 1, background: "var(--color-panel-solid)" }}>
                  <Table.Row>
                    <Table.ColumnHeaderCell style={{ width: "12%", minWidth: 0 }}><TruncatedText weight="medium">Campaign</TruncatedText></Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell style={{ width: "11%", minWidth: 0 }}><TruncatedText weight="medium">Audience</TruncatedText></Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell style={{ width: "11%", minWidth: 0 }}><TruncatedText weight="medium">Template</TruncatedText></Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell style={{ width: "11%", minWidth: 0 }}><TruncatedText weight="medium">Schedule</TruncatedText></Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell style={{ width: "11%", minWidth: 0 }}><TruncatedText weight="medium">Sender</TruncatedText></Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell style={{ width: "10%", minWidth: 0 }}><TruncatedText weight="medium">Delivery</TruncatedText></Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell style={{ width: "9%", minWidth: 0 }}><TruncatedText weight="medium">Status</TruncatedText></Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell style={{ width: "9%", minWidth: 0 }}><TruncatedText weight="medium">Created on</TruncatedText></Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell style={{ width: "9%", minWidth: 0 }}><TruncatedText weight="medium">Updated on</TruncatedText></Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell style={{ width: "7%", minWidth: 0 }}><TruncatedText weight="medium">Actions</TruncatedText></Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {campaigns.map((campaign) => (
                    <Table.Row key={campaign.id}>
                      <Table.Cell style={{ minWidth: 0 }}>
                        <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
                          <TruncatedText weight="medium">{campaign.name}</TruncatedText>
                        </Flex>
                      </Table.Cell>
                      <Table.Cell style={{ minWidth: 0 }}>
                        <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
                          <TruncatedText>{campaign.audienceLabel}</TruncatedText>
                          <TruncatedText size="1" color="gray">{`${campaign.recipientCount.toLocaleString()} estimated recipients`}</TruncatedText>
                        </Flex>
                      </Table.Cell>
                      <Table.Cell style={{ minWidth: 0 }}>
                        <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
                          <TruncatedText>{campaign.templateName}</TruncatedText>
                        </Flex>
                      </Table.Cell>
                      <Table.Cell style={{ minWidth: 0 }}>
                        <TruncatedText>
                          {campaign.sendMode === "scheduled" ? formatDateTime(campaign.scheduledSendAt) : "Send now"}
                        </TruncatedText>
                      </Table.Cell>
                      <Table.Cell style={{ minWidth: 0 }}>
                        <TruncatedText>{campaign.senderPhoneNumber || "—"}</TruncatedText>
                      </Table.Cell>
                      <Table.Cell>
                        <Flex direction="column" align="start" gap="1" style={{ minWidth: 0, lineHeight: 1.2 }}>
                          <TruncatedText>{`${campaign.deliveredCount} / ${campaign.recipientCount}`}</TruncatedText>
                          {campaign.failedCount > 0 ? (
                            <TruncatedText size="1" color="red">{`${campaign.failedCount} failed`}</TruncatedText>
                          ) : null}
                        </Flex>
                      </Table.Cell>
                      <Table.Cell>
                        <Tooltip content={campaign.status === "paused_limit" ? "Sending will resume automatically when WhatsApp capacity is available." : campaign.status}>
                          <Badge color={STATUS_COLORS[campaign.status] || "gray"} variant="soft" style={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{campaign.status === "paused_limit" ? "Paused — WhatsApp limit" : campaign.status}</Badge>
                        </Tooltip>
                      </Table.Cell>
                      <Table.Cell style={{ minWidth: 0 }}>
                        <TruncatedText color="gray">{formatCampaignDateTime(campaign.createdAt)}</TruncatedText>
                      </Table.Cell>
                      <Table.Cell style={{ minWidth: 0 }}>
                        <TruncatedText color="gray">{formatCampaignDateTime(campaign.updatedAt)}</TruncatedText>
                      </Table.Cell>
                      <Table.Cell>
                        <Flex gap="1" wrap="wrap">
                          <Tooltip content={canEditCampaign(campaign) ? "Edit scheduled campaign" : "Only scheduled campaigns can be edited"}>
                            <Button
                              size="1"
                              variant="soft"
                              color="gray"
                              aria-label="Edit scheduled campaign"
                              disabled={!canEditCampaign(campaign)}
                              onClick={() => openEditDialog(campaign)}
                            >
                              <Pencil1Icon width="14" height="14" />
                            </Button>
                          </Tooltip>

                          <Tooltip content={canDeleteCampaign(campaign) ? "Delete scheduled campaign" : "Only scheduled campaigns can be deleted"}>
                            <Button
                              size="1"
                              variant="soft"
                              color="red"
                              aria-label="Delete scheduled campaign"
                              disabled={!canDeleteCampaign(campaign)}
                              onClick={() => setCampaignToDelete(campaign)}
                            >
                              <TrashIcon width="14" height="14" />
                            </Button>
                          </Tooltip>
                        </Flex>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          </Card>
          {pagination.numPages > 1 && (
            <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
              <Text size="2" color="gray">
                Showing {(pagination.currentPage - 1) * 25 + 1} – {Math.min(pagination.currentPage * 25, pagination.count)} of {pagination.count} campaigns
              </Text>
              <Flex gap="2" align="center">
                <Button
                  variant="soft"
                  disabled={!pagination.hasPrevious}
                  onClick={() => setCampaignPage(pagination.currentPage - 1)}
                >
                  <ChevronLeftIcon /> Previous
                </Button>
                <Text size="2">Page {pagination.currentPage} of {pagination.numPages}</Text>
                <Button
                  variant="soft"
                  disabled={!pagination.hasNext}
                  onClick={() => setCampaignPage(pagination.currentPage + 1)}
                >
                  Next <ChevronRightIcon />
                </Button>
              </Flex>
            </Flex>
          )}
          </>
        )}
      </Flex>
      ) : null}

      <Dialog.Root open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <Dialog.Content style={{ display: "flex", flexDirection: "column", maxHeight: "85vh", maxWidth: createDialogMaxWidth }}>
          <Dialog.Title>{isEditingCampaign ? "Edit WhatsApp Campaign" : "Create WhatsApp Campaign"}</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            {isEditingCampaign
              ? "Update this scheduled WhatsApp campaign before it is sent."
              : "Configure the campaign, audience, and send time, then queue it for immediate delivery or schedule it for later."}
          </Dialog.Description>

          <Flex gap="2" wrap="wrap" mt="3">
            {STEP_TITLES.map((_, index) => (
              <StepBadge key={STEP_TITLES[index]} index={index} currentStep={currentStep} />
            ))}
          </Flex>

          <Flex direction="column" gap="4" mt="4" style={{ flex: 1, minHeight: 0, overflow: "auto", paddingRight: 4 }}>
            {currentStep === 1 ? (
              <Flex direction="column" gap="3">
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">Campaign name</Text>
                  <TextField.Root placeholder="Weekend WhatsApp offer" value={campaignForm.name} onChange={(event) => setCampaignForm((previous) => ({ ...previous, name: event.target.value }))} />
                </Flex>

                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">Template</Text>
                  <Select.Root value={campaignForm.templateId} onValueChange={(value) => setCampaignForm((previous) => ({ ...previous, templateId: value, phoneNumberId: "" }))}>
                    <Select.Trigger style={{ width: "100%" }} />
                    <Select.Content>
                      {approvedTemplates.map((template) => (
                        <Select.Item key={template.id} value={getTemplateFormValue(template)}>{template.name}</Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                </Flex>

                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">Sender phone number</Text>
                  <Select.Root
                    value={campaignForm.phoneNumberId}
                    onValueChange={(value) => setCampaignForm((previous) => ({ ...previous, phoneNumberId: value }))}
                    disabled={!selectedTemplate || isLoadingPhoneNumbers || whatsappPhoneNumbers.length === 0}
                  >
                    <Select.Trigger
                      placeholder={isLoadingPhoneNumbers ? "Loading phone numbers..." : "Select sender phone number"}
                      style={{ width: "100%" }}
                    />
                    <Select.Content>
                      {whatsappPhoneNumbers.map((phoneNumber) => (
                        <Select.Item key={phoneNumber.id} value={String(phoneNumber.id)}>
                          {getWhatsappPhoneNumberLabel(phoneNumber)}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                  {!isLoadingPhoneNumbers && selectedTemplate && whatsappPhoneNumbers.length === 0 ? (
                    <Text size="1" color="red">No sender phone numbers were found for this WhatsApp account.</Text>
                  ) : null}
                </Flex>

                {selectedTemplate ? (
                  <Callout.Root color={messagingLimit.color} variant="surface">
                    <Callout.Icon>
                      <InfoCircledIcon />
                    </Callout.Icon>
                    <Callout.Text>
                      <Flex direction="column" gap="1">
                        <Text size="2">
                          WhatsApp messaging tier: {messagingLimit.tier}. {messagingLimit.detail} Messages wait at the limit and resume automatically when capacity is available.
                        </Text>
                        <WhatsappMessagingLimitHelp />
                      </Flex>
                    </Callout.Text>
                  </Callout.Root>
                ) : null}
              </Flex>
            ) : null}

            {currentStep === 2 ? (
              <Flex direction="column" gap="3">
                <Text size="2" weight="medium">Audience source</Text>
                <RadioGroup.Root
                  value={campaignForm.audienceType}
                  onValueChange={(value) => setCampaignForm((previous) => ({
                    ...previous,
                    audienceType: value,
                    segmentIds: value === "segment" ? previous.segmentIds : [],
                    tagIds: value === "tag" ? previous.tagIds : [],
                  }))}
                >
                  <Flex direction="column" gap="3">
                    <Card variant={campaignForm.audienceType === "all_contacts" ? "surface" : "classic"}>
                      <Text as="label" size="2">
                        <Flex justify="between" align="center">
                          <Flex align="center" gap="3">
                            <RadioGroup.Item value="all_contacts" />
                            All WhatsApp-reachable contacts
                          </Flex>
                          <Badge color="gray" variant="surface">{allContactsEstimate.toLocaleString()} contacts</Badge>
                        </Flex>
                      </Text>
                    </Card>

                    <Card variant={campaignForm.audienceType === "segment" ? "surface" : "classic"}>
                      <Flex direction="column" gap="3">
                        <Text as="label" size="2">
                          <Flex align="center" gap="3">
                            <RadioGroup.Item value="segment" />
                            Selected segments
                          </Flex>
                        </Text>

                        {campaignForm.audienceType === "segment" ? (
                          <Flex direction="column" gap="2" style={{ maxHeight: 220, overflowY: "auto", paddingRight: 4 }}>
                            {segments.map((segment) => {
                              const checked = campaignForm.segmentIds.includes(String(segment.id));
                              return (
                                <Card key={segment.id} variant={checked ? "surface" : "classic"}>
                                  <Flex justify="between" align="center" gap="3">
                                    <Flex align="center" gap="3">
                                      <Checkbox checked={checked} onCheckedChange={(value) => handleToggleSegment(segment.id, Boolean(value))} />
                                      <Text weight="medium">{segment.name}</Text>
                                    </Flex>
                                    <Badge color="gray" variant="surface">{Number(segment.contact_count ?? segment.cached_contact_count ?? 0).toLocaleString()}</Badge>
                                  </Flex>
                                </Card>
                              );
                            })}
                          </Flex>
                        ) : null}
                      </Flex>
                    </Card>

                    <Card variant={campaignForm.audienceType === "tag" ? "surface" : "classic"}>
                      <Flex direction="column" gap="3">
                        <Text as="label" size="2">
                          <Flex align="center" gap="3">
                            <RadioGroup.Item value="tag" />
                            Selected tags
                          </Flex>
                        </Text>

                        {campaignForm.audienceType === "tag" ? (
                          <Flex direction="column" gap="2" style={{ maxHeight: 220, overflowY: "auto", paddingRight: 4 }}>
                            {contactTags.length === 0 ? (
                              <Text size="2" color="gray">No tags available.</Text>
                            ) : contactTags.map((tag) => {
                              const checked = campaignForm.tagIds.includes(String(tag.id));
                              return (
                                <Card key={tag.id} variant={checked ? "surface" : "classic"}>
                                  <Flex justify="between" align="center" gap="3">
                                    <Flex align="center" gap="3">
                                      <Checkbox checked={checked} onCheckedChange={(value) => handleToggleTag(tag.id, Boolean(value))} />
                                      <Text weight="medium">{tag.name}</Text>
                                    </Flex>
                                    <Badge color="gray" variant="surface">{Number(tag.contact_count ?? 0).toLocaleString()}</Badge>
                                  </Flex>
                                </Card>
                              );
                            })}
                          </Flex>
                        ) : null}
                      </Flex>
                    </Card>
                  </Flex>
                </RadioGroup.Root>

                <Callout.Root color="amber" variant="surface">
                  <Callout.Icon>
                    <InfoCircledIcon />
                  </Callout.Icon>
                  <Callout.Text>
                    Estimated recipients: {recipientEstimate.toLocaleString()}.
                  </Callout.Text>
                </Callout.Root>
              </Flex>
            ) : null}

            {currentStep === 3 ? (
              <Flex direction="column" gap="3">
                <Text size="2" weight="medium">Send timing</Text>
                <RadioGroup.Root
                  value={campaignForm.sendMode}
                  onValueChange={(value) => {
                    if (isEditingCampaign && value !== "scheduled") {
                      return;
                    }
                    setCampaignForm((previous) => ({
                      ...previous,
                      sendMode: value,
                      scheduledSendAt: value === "scheduled" ? previous.scheduledSendAt : "",
                    }));
                  }}
                >
                  <Flex direction="column" gap="3">
                    <Card variant={campaignForm.sendMode === "now" ? "surface" : "classic"}>
                      <Text as="label" size="2">
                        <Flex align="center" gap="3">
                          <RadioGroup.Item value="now" disabled={isEditingCampaign} />
                          Send immediately
                        </Flex>
                      </Text>
                    </Card>

                    <Card variant={campaignForm.sendMode === "scheduled" ? "surface" : "classic"}>
                      <Flex direction="column" gap="3">
                        <Text as="label" size="2">
                          <Flex align="center" gap="3">
                            <RadioGroup.Item value="scheduled" />
                            Schedule send window
                          </Flex>
                        </Text>

                        {campaignForm.sendMode === "scheduled" ? (
                          <DateTimePicker
                            value={campaignForm.scheduledSendAt ? new Date(campaignForm.scheduledSendAt) : null}
                            onChange={(date) => setCampaignForm((previous) => ({
                              ...previous,
                              scheduledSendAt: date ? date.toISOString() : "",
                            }))}
                            disablePast
                          />
                        ) : null}
                      </Flex>
                    </Card>
                  </Flex>
                </RadioGroup.Root>
              </Flex>
            ) : null}

            {currentStep === 4 ? (
              <Flex direction="column" gap="3">
                <Text size="3" weight="bold">Campaign summary</Text>

                <Flex gap="3" wrap="wrap">
                  <Card style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)", flex: "1 1 260px" }}>
                    <Text size="1" color="gray">Campaign</Text>
                    <Text weight="medium" style={{ wordBreak: "break-word" }}>{campaignForm.name || "Untitled campaign"}</Text>
                    <Badge size="2" color="green" variant="soft">WhatsApp</Badge>
                  </Card>

                  <Card style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)", flex: "1 1 260px" }}>
                    <Text size="1" color="gray">Audience</Text>
                    <Text weight="medium">{recipientEstimate.toLocaleString()} contacts</Text>
                    <Text size="1" color="gray" style={{ wordBreak: "break-word" }}>
                      {campaignForm.audienceType === "all_contacts"
                        ? "All WhatsApp-reachable contacts"
                        : campaignForm.audienceType === "tag"
                          ? selectedTags.map((tag) => tag.name).join(", ") || "No tags selected"
                          : selectedSegments.map((segment) => segment.name).join(", ") || "No segments selected"}
                    </Text>
                  </Card>
                </Flex>

                <Flex gap="3" wrap="wrap">
                  <Card style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)", flex: "1 1 260px" }}>
                    <Text size="1" color="gray">Launch type</Text>
                    <Text weight="medium">{campaignForm.sendMode === "scheduled" ? "Scheduled" : "Send immediately"}</Text>
                    <Text size="1" color="gray">{campaignForm.sendMode === "scheduled" ? formatDateTime(campaignForm.scheduledSendAt) : "Queued as soon as you save the campaign"}</Text>
                  </Card>

                  <Card style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)", flex: "1 1 260px" }}>
                    <Text size="1" color="gray">Template</Text>
                    <Text weight="medium" style={{ wordBreak: "break-word" }}>{selectedTemplate?.name || "No template selected"}</Text>
                    <Text size="1" color="gray" style={{ wordBreak: "break-word" }}>
                      From {getWhatsappPhoneNumberLabel(selectedPhoneNumber) || "No sender selected"}
                    </Text>
                  </Card>
                </Flex>

                <Card>
                  <Flex direction="column" gap="3">
                    <Text size="3" weight="bold">Message preview</Text>
                    {selectedTemplate ? (
                      <Box style={{ minHeight: 320 }}>
                        <MarketingWhatsappPreview template={selectedTemplate} />
                      </Box>
                    ) : (
                      <Text size="2" color="gray">Choose a template to render the preview.</Text>
                    )}
                  </Flex>
                </Card>
              </Flex>
            ) : null}
          </Flex>

          <Flex gap="3" mt="5" justify="between" align="center">
            <Button variant="soft" color="gray" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
            <Flex gap="2">
              {currentStep > 1 ? (
                <Button variant="soft" color="gray" onClick={() => setCurrentStep((step) => Math.max(step - 1, 1))}>
                  Back
                </Button>
              ) : null}

              {currentStep < STEP_TITLES.length ? (
                <Button onClick={() => setCurrentStep((step) => Math.min(step + 1, STEP_TITLES.length))} disabled={!stepIsValid}>
                  Next
                </Button>
              ) : (
                <Button onClick={handleConfirm} disabled={!selectedTemplate || !campaignForm.phoneNumberId || isSavingCampaign} loading={isSavingCampaign}>
                  {isEditingCampaign ? "Save changes" : "Save campaign"}
                </Button>
              )}
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <AlertDialog.Root open={!!campaignToDelete} onOpenChange={(open) => !open && setCampaignToDelete(null)}>
        <AlertDialog.Content maxWidth="420px">
          <AlertDialog.Title>Delete WhatsApp Campaign</AlertDialog.Title>
          <AlertDialog.Description size="2">
            Are you sure you want to delete{" "}
            <Text weight="bold">{campaignToDelete?.name}</Text>?
            This action cannot be undone.
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                variant="solid"
                color="red"
                onClick={handleDeleteCampaign}
                loading={deleteCampaignMutation.isPending}
              >
                Delete
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>

      {!dialogOnly ? <ToastAlert /> : null}
    </>
  );
}
